/**
 * Status formatting utilities
 * Provides consistent status color mapping and label formatting
 */

/**
 * Material-UI Chip color types
 */
export type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

/**
 * Status color mapping for bidding processes (NEW: Based on backend enums)
 * Status de Licitação:
 * 1 - EM ANDAMENTO (warning)
 * 2 - ANULADA (error)
 * 3 - CANCELADA (error)
 * 4 - DESERTA (default)
 * 5 - ENCERRADA (info)
 * 6 - FRUSTRADA (default)
 * 7 - HOMOLOGADA (success)
 * 8 - SUSPENSA (warning)
 * 9 - REVOGADA (error)
 * @param status - Status string or number
 * @returns Material-UI chip color
 * @example
 * getBiddingStatusColor(7) // Returns 'success' (HOMOLOGADA)
 * getBiddingStatusColor('CANCELADA') // Returns 'error'
 */
export const getBiddingStatusColor = (status?: string | number | null): ChipColor => {
  if (!status) return 'default';

  // Handle numeric status first
  if (typeof status === 'number') {
    switch (status) {
      case 1: // EM ANDAMENTO
        return 'warning';
      case 2: // ANULADA
      case 3: // CANCELADA
      case 9: // REVOGADA
        return 'error';
      case 4: // DESERTA
      case 6: // FRUSTRADA
        return 'default';
      case 5: // ENCERRADA
        return 'info';
      case 7: // HOMOLOGADA
        return 'success';
      case 8: // SUSPENSA
        return 'warning';
      default:
        return 'default';
    }
  }

  // Handle string-based status
  const statusStr = String(status).toLowerCase();

  if (statusStr.includes('homologada')) return 'success';
  if (statusStr.includes('andamento') || statusStr.includes('suspensa')) return 'warning';
  if (statusStr.includes('anulada') || statusStr.includes('cancelada') || statusStr.includes('revogada')) return 'error';
  if (statusStr.includes('encerrada')) return 'info';
  if (statusStr.includes('deserta') || statusStr.includes('frustrada')) return 'default';

  return 'default';
};

/**
 * Status color mapping for audit processes (NEW: Based on backend enums)
 * Situação da Auditoria:
 * 1 - Entregue (success)
 * 2 - Sobrestado (warning)
 * 3 - Julgado (info)
 * @param status - Status number or string
 * @returns Material-UI chip color
 * @example
 * getAuditStatusColor(1) // Returns 'success' (Entregue)
 * getAuditStatusColor(3) // Returns 'info' (Julgado)
 */
export const getAuditStatusColor = (status?: string | number | null): ChipColor => {
  if (status === null || status === undefined) return 'default';

  const statusNum = typeof status === 'number' ? status : parseInt(String(status), 10);

  if (isNaN(statusNum)) {
    // Handle string-based status
    const statusStr = String(status).toLowerCase();
    if (statusStr.includes('entregue')) return 'success';
    if (statusStr.includes('sobrestado')) return 'warning';
    if (statusStr.includes('julgado')) return 'info';
    return 'default';
  }

  // Handle numeric status
  switch (statusNum) {
    case 1: // Entregue
      return 'success';
    case 2: // Sobrestado
      return 'warning';
    case 3: // Julgado
      return 'info';
    default:
      return 'default';
  }
};

/**
 * Status label mapping for audit processes (NEW: Based on backend enums)
 * Situação da Auditoria:
 * 1 - Entregue
 * 2 - Sobrestado
 * 3 - Julgado
 * @param status - Status number
 * @returns Human-readable status label
 * @example
 * getAuditStatusLabel(1) // Returns 'Entregue'
 * getAuditStatusLabel(3) // Returns 'Julgado'
 */
export const getAuditStatusLabel = (status?: number | null): string => {
  if (status === null || status === undefined) return 'Não especificado';

  const statusMap: { [key: number]: string } = {
    1: 'Entregue',
    2: 'Sobrestado',
    3: 'Julgado',
  };

  return statusMap[status] || 'Não especificado';
};

/**
 * Generic status color mapper
 * Provides a general-purpose status to color mapping
 * @param status - Status string or number
 * @param customMap - Optional custom status mapping
 * @returns Material-UI chip color
 * @example
 * getStatusColor('ativo') // Returns 'success'
 * getStatusColor('inativo') // Returns 'error'
 * getStatusColor('pendente') // Returns 'warning'
 */
export const getStatusColor = (
  status?: string | number | null,
  customMap?: { [key: string]: ChipColor }
): ChipColor => {
  if (!status) return 'default';

  const statusStr = String(status).toLowerCase();

  // Check custom map first
  if (customMap && customMap[statusStr]) {
    return customMap[statusStr];
  }

  // Default mappings for common status terms
  // Success states
  if (
    statusStr.includes('ativo') ||
    statusStr.includes('aprovado') ||
    statusStr.includes('concluído') ||
    statusStr.includes('concluída') ||
    statusStr.includes('sucesso') ||
    statusStr.includes('homologado') ||
    statusStr.includes('publicado') ||
    statusStr.includes('aberta')
  ) {
    return 'success';
  }

  // Warning states
  if (
    statusStr.includes('pendente') ||
    statusStr.includes('aguardando') ||
    statusStr.includes('em análise') ||
    statusStr.includes('em andamento') ||
    statusStr.includes('suspensa')
  ) {
    return 'warning';
  }

  // Error states
  if (
    statusStr.includes('inativo') ||
    statusStr.includes('cancelado') ||
    statusStr.includes('cancelada') ||
    statusStr.includes('rejeitado') ||
    statusStr.includes('erro') ||
    statusStr.includes('falha')
  ) {
    return 'error';
  }

  // Info states
  if (
    statusStr.includes('encerrado') ||
    statusStr.includes('encerrada') ||
    statusStr.includes('arquivado') ||
    statusStr.includes('processando')
  ) {
    return 'info';
  }

  return 'default';
};

/**
 * Publication status color mapper
 * @param published - Boolean indicating if published
 * @returns Material-UI chip color
 */
export const getPublicationStatusColor = (published?: boolean | null): ChipColor => {
  if (published === null || published === undefined) return 'default';
  return published ? 'success' : 'warning';
};

/**
 * Publication status label
 * @param published - Boolean indicating if published
 * @returns Human-readable status label
 */
export const getPublicationStatusLabel = (published?: boolean | null): string => {
  if (published === null || published === undefined) return 'Não especificado';
  return published ? 'Publicado' : 'Não Publicado';
};

/**
 * Active/Inactive status color
 * @param active - Boolean indicating if active
 * @returns Material-UI chip color
 */
export const getActiveStatusColor = (active?: boolean | null): ChipColor => {
  if (active === null || active === undefined) return 'default';
  return active ? 'success' : 'error';
};

/**
 * Active/Inactive status label
 * @param active - Boolean indicating if active
 * @returns Human-readable status label
 */
export const getActiveStatusLabel = (active?: boolean | null): string => {
  if (active === null || active === undefined) return 'Não especificado';
  return active ? 'Ativo' : 'Inativo';
};

/**
 * Priority level color mapper
 * @param priority - Priority level (low, medium, high, urgent) or number (1-4)
 * @returns Material-UI chip color
 */
export const getPriorityColor = (priority?: string | number | null): ChipColor => {
  if (!priority) return 'default';

  const priorityStr = String(priority).toLowerCase();

  // Handle string priorities
  if (priorityStr.includes('baixa') || priorityStr === '1') return 'info';
  if (priorityStr.includes('média') || priorityStr.includes('media') || priorityStr === '2')
    return 'primary';
  if (priorityStr.includes('alta') || priorityStr === '3') return 'warning';
  if (priorityStr.includes('urgente') || priorityStr.includes('crítica') || priorityStr === '4')
    return 'error';

  return 'default';
};

/**
 * Priority level label mapper
 * @param priority - Priority number (1-4)
 * @returns Human-readable priority label
 */
export const getPriorityLabel = (priority?: number | null): string => {
  if (priority === null || priority === undefined) return 'Não especificado';

  const priorityMap: { [key: number]: string } = {
    1: 'Baixa',
    2: 'Média',
    3: 'Alta',
    4: 'Urgente',
  };

  return priorityMap[priority] || 'Não especificado';
};

// ============================================================================
// NEW ENUM MAPPERS - Based on Backend API
// ============================================================================

/**
 * Bidding status label mapping (NEW)
 * Status de Licitação:
 * 1 - EM ANDAMENTO
 * 2 - ANULADA
 * 3 - CANCELADA
 * 4 - DESERTA
 * 5 - ENCERRADA
 * 6 - FRUSTRADA
 * 7 - HOMOLOGADA
 * 8 - SUSPENSA
 * 9 - REVOGADA
 * @param status - Status number
 * @returns Human-readable status label
 * @example
 * getBiddingStatusLabel(7) // Returns 'HOMOLOGADA'
 */
export const getBiddingStatusLabel = (status?: number | null): string => {
  if (status === null || status === undefined) return 'Não especificado';

  const statusMap: { [key: number]: string } = {
    1: 'EM ANDAMENTO',
    2: 'ANULADA',
    3: 'CANCELADA',
    4: 'DESERTA',
    5: 'ENCERRADA',
    6: 'FRUSTRADA',
    7: 'HOMOLOGADA',
    8: 'SUSPENSA',
    9: 'REVOGADA',
  };

  return statusMap[status] || 'Não especificado';
};

/**
 * Audit regulatory body label mapping (NEW)
 * Órgão Regulamentador:
 * 1 - TCM (Tribunal de Contas dos Municípios)
 * 2 - TCE (Tribunal de Contas do Estado)
 * 3 - TCU (Tribunal de Contas da União)
 * 4 - CGU (Controladoria-Geral da União)
 * @param regulatoryBody - Regulatory body number
 * @returns Human-readable regulatory body label
 * @example
 * getAuditRegulatoryBodyLabel(1) // Returns 'TCM'
 */
export const getAuditRegulatoryBodyLabel = (regulatoryBody?: number | null): string => {
  if (regulatoryBody === null || regulatoryBody === undefined) return 'Não especificado';

  const regulatoryBodyMap: { [key: number]: string } = {
    1: 'TCM',
    2: 'TCE',
    3: 'TCU',
    4: 'CGU',
  };

  return regulatoryBodyMap[regulatoryBody] || 'Não especificado';
};

/**
 * Audit regulatory body full name mapping (NEW)
 * @param regulatoryBody - Regulatory body number
 * @returns Full name of regulatory body
 * @example
 * getAuditRegulatoryBodyFullName(1) // Returns 'Tribunal de Contas dos Municípios'
 */
export const getAuditRegulatoryBodyFullName = (regulatoryBody?: number | null): string => {
  if (regulatoryBody === null || regulatoryBody === undefined) return 'Não especificado';

  const regulatoryBodyMap: { [key: number]: string } = {
    1: 'Tribunal de Contas dos Municípios',
    2: 'Tribunal de Contas do Estado',
    3: 'Tribunal de Contas da União',
    4: 'Controladoria-Geral da União',
  };

  return regulatoryBodyMap[regulatoryBody] || 'Não especificado';
};

/**
 * Governmental project type label mapping (NEW)
 * Tipo de Projeto Governamental:
 * 1 - Programa
 * 2 - Programas (plural - parece redundante, mas está no enum)
 * 3 - Atividade
 * 4 - Outros
 * @param projectType - Project type number
 * @returns Human-readable project type label
 * @example
 * getGovernmentalProjectTypeLabel(1) // Returns 'Programa'
 */
export const getGovernmentalProjectTypeLabel = (projectType?: number | null): string => {
  if (projectType === null || projectType === undefined) return 'Não especificado';

  const typeMap: { [key: number]: string } = {
    1: 'Programa',
    2: 'Programas',
    3: 'Atividade',
    4: 'Outros',
  };

  return typeMap[projectType] || 'Não especificado';
};

/**
 * Governmental project type color mapping (NEW)
 * @param projectType - Project type number
 * @returns Material-UI chip color
 */
export const getGovernmentalProjectTypeColor = (projectType?: number | null): ChipColor => {
  if (projectType === null || projectType === undefined) return 'default';

  switch (projectType) {
    case 1: // Programa
    case 2: // Programas
      return 'primary';
    case 3: // Atividade
      return 'info';
    case 4: // Outros
      return 'default';
    default:
      return 'default';
  }
};
